/**
 * Die Geräteverwaltung — der Aufbau aus `ui_kits/dashboard/DevicesScreen.jsx`.
 *
 * Zwei Ebenen, wie dort: oben die **Integrationen** als Kartenraster, darunter
 * die **Geräte** als Tabelle. Die Reihenfolge ist die fachliche Reihenfolge —
 * ein Gerät kommt immer über eine Integration herein, auch das eigene.
 *
 * Vom Kit übernommen sind die Maße (1100 px breit, 26 px zwischen den
 * Abschnitten), die Abschnittsköpfe mit der Aktion rechts auf der Grundlinie und
 * die Spalten der Tabelle bis auf zwei.
 *
 * **Diese zwei weichen ab, und zwar aus demselben Grund wie im Dashboard: Es gibt
 * die Zahl nicht.** Das Kit zeigt rechts „Aktuell" mit einem Momentanwert und
 * daneben ein Menü mit drei Punkten. Den Momentanwert gibt es erst mit HM-010 —
 * an seiner Stelle stehen die Kanäle, die das Gerät mitbringt, denn die gibt es
 * wirklich. Und statt eines Menüs, hinter dem nichts läge, steht der
 * **Zustand** — die Erreichbarkeit und, sobald ein Gerät eines meldet, ein
 * verfügbares **Update**. Ein Knopf, der nichts tut, ist schlimmer als kein
 * Knopf.
 *
 * **Die Update-Marke steht hier, weil die Frage der Liste gilt und nicht dem
 * einzelnen Gerät.** Sie stand bislang nur in der Firmware-Kachel einer
 * Detailseite; „hat irgendeines meiner Geräte ein Update" hieß damit, jedes
 * einzeln zu öffnen. Sie erscheint nur, wenn das Gerät es gemeldet hat —
 * Schweigen ist keine Entwarnung (Regel 9, `updateBadge`).
 *
 * Dort stand bis zuletzt die **Betriebsart** — sie ist verschwunden, weil sie in
 * dieser Anwendung an jedem Gerät dasselbe sagt: Die Web-App spricht mit der
 * Cloud, und ob eine App ihre Werte stattdessen direkt vom Gerät im LAN holt,
 * ist eine Frage der Mobile-App. Eine Spalte, die überall „Cloud" heißt,
 * unterscheidet nichts.
 *
 * **Die Symbole sind neutral gefärbt, anders als im Kit.** Dort trägt jede Zeile
 * eine Spartenfarbe — Wärme, Wasser, Solar. Umgesetzt ist Strom, und eine Farbe,
 * die nur einen Wert kennt, ist keine Unterscheidung, sondern Dekoration
 * (ADR 0013: Farbe trägt Bedeutung oder sie gehört nicht hin).
 *
 * Anders als das Kit ist dieser Bereich nicht nur Anzeige: Geräte lassen sich
 * über ihren **Kopplungscode** hinzufügen (HM-035).
 *
 * **Und über der Geräteliste stehen die offenen Funde des Gateways**
 * (`components/gateway-section.tsx`, HM-109, HM-117). Was es im Heimnetz
 * gefunden hat, ist eine Frage und noch keine Datenquelle — und eine Frage
 * gehört an die Stelle, an der man Geräte anbindet. **Nur die Frage:** Seit
 * HM-117 steht hier weder der Kopf des Gateways noch seine Liste der
 * zugelassenen und abgelehnten Geräte. Das Gateway ist selbst ein Gerät und
 * steht in der Tabelle darunter, seine zugelassenen Knoten ebenso, und die
 * vollständige Liste gibt es auf seiner Seite. Was hier bleibt, ist das
 * Einzige, was in der Tabelle darunter nicht vorkommen kann, weil es dafür noch
 * kein Gerät gibt — und ohne offenen Fund ist der Abschnitt weg.
 *
 * **Integrationen lassen sich hier nicht einrichten, und das ist kein Rückstand.**
 * Die einzige Integration, die es heute gibt, ist die Cloud-Anbindung — und die
 * entsteht mit der Anlage (ADR 0017). `cts_local` und `manual_input` stehen zwar
 * in der Registry, haben aber noch keinen Einrichtungsdialog: Ein Knopf, der sie
 * mit leerer Konfiguration anlegte, versprach eine Datenquelle, die nichts
 * liefert. Der Abschnitt ist deshalb Anzeige, bis HM-013 den aus dem
 * `configSchema` erzeugten Dialog bringt.
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  breakpoints,
  Button,
  Card,
  EmptyState,
  Input,
  icons,
  Select,
  useNarrow,
} from "@hausmeter/ui";
import { isTranslationKey, t } from "@hausmeter/i18n";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { HintList } from "../components/hint-list.js";
import {
  floodingBadge,
  intakeNotice,
  reachabilityBadge,
  updateBadge,
} from "../lib/device-report.js";
import { gatewayRefusalLabel } from "../lib/device-labels.js";
import { deviceTypeLabel, isGatewayDevice, productLine, productName } from "../lib/device-type.js";
import { navigate, routePath, type Route } from "../lib/router.js";
import {
  useDevices,
  useIntakeStatus,
  useIntegrationTypes,
  useIntegrations,
  useSiteGateway,
  useSites,
} from "../lib/queries.js";
import { SectionHeading } from "../components/section.js";
import { GatewayFindings } from "../components/gateway-section.js";
import { useSiteAccess } from "../lib/site-access.js";

type Device = ApiComponents["schemas"]["Device"];
type IntegrationInstance = ApiComponents["schemas"]["IntegrationInstance"];
type ClaimRequestSummary = ApiComponents["schemas"]["ClaimRequestSummary"];

/** Der Zustand einer Integration in Worten. Der Rohwert bleibt als Titel daran hängen. */
const INTEGRATION_LABELS: Record<string, string> = {
  active: "verbunden",
  degraded: "eingeschränkt",
  configuring: "wird eingerichtet",
  error: "gestört",
  disabled: "ausgeschaltet",
};

/**
 * `configuring` ist ausdrücklich **kein** Fehler: Eine Integration, die noch
 * eingerichtet wird, hat noch nichts falsch gemacht.
 */
const INTEGRATION_TONES: Record<string, "success" | "warning" | "danger" | "neutral"> = {
  active: "success",
  degraded: "warning",
  configuring: "neutral",
  error: "danger",
  disabled: "neutral",
};

export function DeviceManagementScreen({
  siteId,
  initialClaimCode = null,
  onClaimCodeUsed,
}: {
  siteId: string | null;
  /** Aus dem Kopplungslink des Geräts (`…/verbinden?code=…`). */
  initialClaimCode?: string | null;
  onClaimCodeUsed?: () => void;
}) {
  const integrations = useIntegrations(siteId);
  const devices = useDevices(siteId);
  /**
   * **Hier fällt ein Ausfall der Annahmestelle zuerst auf**, denn hier stehen
   * alle Geräte untereinander — und sie werden dann alle gleichzeitig still.
   * Ohne diese Auskunft sähe das aus wie ein Schaden an der Anlage.
   */
  const intake = useIntakeStatus();
  const intakeStopped = intakeNotice(intake.data);
  const notices = intakeStopped === null ? [] : [intakeStopped];
  const types = useIntegrationTypes();
  // Was die Teilung nicht erlaubt, fehlt (HM-160, Regel 10): Koppeln ist Schreiben.
  const rights = useSiteAccess();

  /**
   * Das Formular liegt **hinter** einem Knopf, wie im Kit. Vorher stand es
   * dauerhaft offen — eine Eingabemaske unter einer Liste, und die meiste Zeit
   * will man sie nicht ausfüllen, sondern nachsehen, ob ein Gerät sendet.
   */
  const [deviceFormOpen, setDeviceFormOpen] = useState(
    // Wer über den Kopplungslink kommt, sieht das Formular sofort offen — mit
    // dem Code darin. Ein zusätzlicher Klick auf „Gerät hinzufügen" wäre eine
    // Hürde vor dem, wofür er den Link geöffnet hat.
    initialClaimCode !== null,
  );

  const integrationList = integrations.data?.items ?? [];
  const deviceList = devices.data?.items ?? [];

  /** Über welche Integration ein Gerät hereinkommt — im Kit die Spalte „Quelle". */
  const integrationNames = new Map(
    integrationList.map((integration) => [integration.id, integration.name]),
  );

  /** Wie viele Geräte hängen an welcher Integration — einmal gezählt, nicht je Karte. */
  const devicesPerIntegration = new Map<string, number>();
  for (const device of deviceList) {
    if (device.integrationInstanceId === null) continue;
    const current = devicesPerIntegration.get(device.integrationInstanceId) ?? 0;
    devicesPerIntegration.set(device.integrationInstanceId, current + 1);
  }

  /**
   * `labelKey` ist der Übersetzungsschlüssel aus der Registry, und der Vertrag
   * sagt das seit OQ-026 auch so. Ein Schlüssel, den der Katalog nicht kennt,
   * ist ein neuer Typ ohne Übersetzung — er steht dann wörtlich da, und man
   * sieht, welcher fehlt, statt „unbekannt" zu lesen.
   */
  const typeLabels = new Map(
    types.data?.types.map((type) => [
      type.key,
      isTranslationKey(type.labelKey) ? t(type.labelKey) : type.labelKey,
    ]) ?? [],
  );

  return (
    /**
     * Die Maße des Kits: zwei Abschnitte, 26 px dazwischen, höchstens 1100 px
     * breit. Ohne die Begrenzung zieht die Gerätetabelle auf einem breiten
     * Bildschirm ihre Spalten auseinander, bis Name und Quelle nichts mehr
     * miteinander zu tun zu haben scheinen.
     */
    <div style={{ display: "flex", flexDirection: "column", gap: 26, maxWidth: 1100 }}>
      {/**
       * **Über beiden Abschnitten und nicht in der Tabelle.** Die Aussage gilt
       * keinem einzelnen Gerät, sondern allen zugleich; eine Marke je Zeile
       * wiederholte sie zehnmal und beantwortete die Frage trotzdem nicht, die
       * jemand vor dieser Liste hat.
       */}
      <HintList hints={notices} />

      {/**
       * **Unmittelbar über der Geräteliste**, und die Stelle ist die Aussage
       * (HM-117): Das sind die, die hinein wollen — darunter stehen die, die
       * drin sind. Er erscheint nur mit einem offenen Fund; alles andere über
       * das Gateway steht auf dessen Seite.
       */}
      <GatewayFindings siteId={siteId} />

      <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
        <SectionHeading
          title="Geräte"
          subtitle="Jedes Gerät gehört einer Anlage und bringt seine Kanäle mit."
          action={
            rights.canWrite ? (
              <Button
                variant="secondary"
                size="sm"
                disabled={siteId === null}
                onClick={() => setDeviceFormOpen(!deviceFormOpen)}
              >
                <icons.IconPlus size={15} />
                Gerät hinzufügen
              </Button>
            ) : undefined
          }
        />

        {deviceFormOpen && rights.canWrite && (
          <DeviceClaimForm
            siteId={siteId}
            initialCode={initialClaimCode}
            onDone={() => {
              setDeviceFormOpen(false);
              onClaimCodeUsed?.();
            }}
          />
        )}

        <Card padding="none">
          {devices.isPending && <div style={{ padding: "var(--sp-4)" }}>Wird geladen …</div>}

          {!devices.isPending && deviceList.length === 0 && (
            <EmptyState title="Noch kein Gerät" icon={<icons.IconDevice size={30} />}>
              {rights.canWrite
                ? "Schalte am Gerät die Cloud-Anbindung ein. Es zeigt dann einen Kopplungscode, den du hier über „Gerät hinzufügen“ bestätigst."
                : "An dieser Anlage ist noch kein Gerät gekoppelt."}
            </EmptyState>
          )}

          {/* `siteId !== null` ist keine zusätzliche Bedingung, sondern dieselbe:
              Ohne Anlage ist die Abfrage abgeschaltet und die Liste leer
              (ADR 0017). Der Typprüfer weiß das nicht, und die Zeilen brauchen
              die Anlage für ihre Adresse. */}
          {siteId !== null && deviceList.length > 0 && (
            <DeviceTable siteId={siteId} devices={deviceList} sources={integrationNames} />
          )}
        </Card>
      </section>

      <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
        {/**
         * Ohne Aktion rechts: Die Cloud-Anbindung entsteht mit der Anlage, und
         * einen zweiten Typ zum Einrichten gibt es heute nicht (HM-013).
         *
         * **Unter den Geräten, nicht darüber** (UI-Durchsicht U8): Hier lässt sich
         * nichts tun, und wer die Seite öffnet, sucht seine Geräte.
         */}
        <SectionHeading
          title="Integrationen"
          subtitle="Datenquellen, über die Geräte angebunden werden — auch die eigenen."
        />

        {integrations.isPending && <Card padding="sm">Wird geladen …</Card>}

        {/**
         * Dieser Fall sollte nicht eintreten: Jede Anlage bekommt ihre
         * Cloud-Anbindung beim Anlegen (ADR 0017). Steht hier trotzdem nichts,
         * ist das kein leerer Anfangszustand, den der Nutzer füllen soll,
         * sondern eine Auffälligkeit — und die Fläche sagt genau das.
         */}
        {!integrations.isPending && integrationList.length === 0 && (
          <Card>
            <EmptyState title="Keine Integration gefunden" icon={<icons.IconPlug size={30} />}>
              Diese Anlage sollte ihre Cloud-Anbindung mitbringen. Fehlt sie, kommt kein Wert herein
              — bitte melde dich beim Betreiber.
            </EmptyState>
          </Card>
        )}

        {integrationList.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 260px), 1fr))",
              gap: "var(--sp-3)",
            }}
          >
            {integrationList.map((integration) => (
              <IntegrationCard
                key={integration.id}
                integration={integration}
                typeLabel={typeLabels.get(integration.type) ?? integration.type}
                deviceCount={devicesPerIntegration.get(integration.id) ?? 0}
              />
            ))}
          </div>
        )}
      </section>

    </div>
  );
}

/**
 * Eine Integration als Karte.
 *
 * Das Symbol ist **neutral gefärbt**, anders als im Kit. Dort trägt jede
 * Integration eine eigene bunte Farbe — die aber nichts bedeutet: Sie ist weder
 * Sparte noch Größe, und der Nutzer lernt sie folglich nicht. Was die Karte
 * wirklich zu sagen hat, sagt die Zustandsmarke.
 *
 * **„Zustand prüfen" gibt es nicht mehr, und die Fußzeile mit ihm.** Die einzige
 * Integration, die es heute gibt, ist die verwaltete Cloud-Anbindung, und deren
 * `healthCheck` antwortet mit einem festen Satz: Die Verbindung zum Broker hält
 * der Ingest, der Adapter hat keinen eigenen Zustand. Ein Knopf, der einen
 * Zustand zu prüfen verspricht und eine Konstante zurückgibt, ist an genau der
 * Stelle falsch, an der es wirklich einen gibt — den der Annahmestelle, und den
 * sagt der Kasten darüber (HM-056). `lastSyncAt` stand aus demselben Grund
 * dauerhaft auf „noch keine Daten": Es schreibt niemand, während im Minutentakt
 * Werte durch dieselbe Integration hereinkommen. `GET
 * /integrations/{integrationId}/health` bleibt — ein Adapter mit echtem Zustand
 * (`cts_local` fragt ein Gerät im LAN) braucht ihn, sobald es ihn zu zeigen gibt.
 *
 * **Und „gehört zur Anlage" steht nicht mehr daneben.** Die Marke erklärte, warum
 * an dieser Karte weniger steht als an den anderen — mit dem Knopf ist die Frage
 * weg, die sie beantwortete, und an einer Ansicht, in der sich ohnehin keine
 * Integration einrichten lässt, unterscheidet sie nichts. Verwaltet bleibt, was
 * es ist (ADR 0017); der Server weist Löschen und Anlegen ab, und das ist der
 * Ort, an dem es zählt. Zu sagen ist sie erst wieder, wenn HM-013 den
 * Einrichtungsdialog bringt und neben der verwalteten Karte eine steht, die man
 * entfernen kann.
 */
function IntegrationCard({
  integration,
  typeLabel,
  deviceCount,
}: {
  integration: IntegrationInstance;
  typeLabel: string;
  deviceCount: number;
}) {
  const failed = integration.status === "error";

  return (
    <Card padding="none">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--sp-3)",
          padding: "var(--sp-4)",
          height: "100%",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--sp-3)" }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 38,
              height: 38,
              flexShrink: 0,
              borderRadius: "var(--radius-md)",
              background: "var(--surface-inset)",
              color: "var(--text-muted)",
            }}
          >
            <icons.IconPlug size={19} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--text-strong)", ...CLIP }}>
              {integration.name}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", ...CLIP }}>{typeLabel}</div>
          </div>
        </div>

        <div
          style={{ display: "flex", alignItems: "center", gap: "var(--sp-2)", flexWrap: "wrap" }}
        >
          <Badge
            tone={INTEGRATION_TONES[integration.status] ?? "neutral"}
            title={integration.status}
          >
            {INTEGRATION_LABELS[integration.status] ?? integration.status}
          </Badge>
          <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
            {deviceCount === 1 ? "1 Gerät" : `${deviceCount} Geräte`}
          </span>
        </div>

        {failed && integration.lastErrorMessage !== null && (
          <p style={{ margin: 0, fontSize: 12, color: "var(--hm-danger)" }}>
            {integration.lastErrorMessage}
          </p>
        )}
      </div>
    </Card>
  );
}

/**
 * Die Geräte als Tabelle. Kopfzeile und Zeilen teilen sich dieselbe Rasterdefinition.
 *
 * **Die Spalte „Quelle" ist neu und war vorher nicht möglich:** Bis HM-034 hing
 * kein Gerät an einer Integration — `integrationInstanceId` blieb beim
 * Beanspruchen leer, und eine Spalte, die für jede Zeile „—" zeigt, ist keine.
 * Seither bekommt jedes Cloud-Gerät die Cloud-Anbindung seiner Anlage.
 *
 * **Jede Zeile führt zum Gerät** — im Kit über einen Klick auf die Zeile. Hier
 * hängt der Weg am **Gerätenamen als echtem Link**, und die Zeile folgt ihm nur
 * für die Maus. Der Unterschied ist nicht kosmetisch: Ein `<div onClick>` ist
 * mit der Tastatur nicht erreichbar und meldet einer Sprachausgabe nichts —
 * derselbe Grund, aus dem die Komponenten in `@hausmeter/ui` übersetzt und nicht
 * kopiert wurden. Und weil es ein `href` ist, lässt sich ein Gerät im neuen Tab
 * öffnen und sein Link weitergeben; das geht erst, seit jede Ansicht eine
 * Adresse hat (HM-037).
 */
function DeviceTable({
  siteId,
  devices,
  sources,
}: {
  siteId: string;
  devices: readonly Device[];
  sources: ReadonlyMap<string, string>;
}) {
  /**
   * **Schmal trägt die Liste zwei Spalten statt sechs**, wie im Kit
   * (`DevicesScreen.jsx`). Sechs Spalten auf 380 Pixeln sind keine Tabelle
   * mehr, sondern sechs abgeschnittene Wörter.
   *
   * Was wegfällt, fällt nicht weg: Erzeugnis und Quelle rücken unter den Namen.
   * Die **Kanäle** bleiben draußen — eine Aufzählung mehrerer Namen in einer
   * zweiten Zeile wäre länger als die Zeile selbst, und sie steht vollständig
   * auf der Detailseite des Geräts. Und der Chevron entfällt: Er wiederholt nur,
   * wohin die Zeile führt, und ist die erste Spalte, die man an dieser Stelle
   * entbehren kann.
   */
  const narrow = useNarrow(breakpoints.table);
  const columns = narrow ? "minmax(0, 1fr) auto" : "1.7fr 0.9fr 1.1fr 1.1fr 1fr 28px";

  return (
    <div role="table" aria-label="Geräte">
      <div
        role="row"
        style={{
          display: "grid",
          gridTemplateColumns: columns,
          gap: "var(--sp-3)",
          alignItems: "center",
          padding: "11px var(--sp-5)",
          borderBottom: "1px solid var(--border-subtle)",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "var(--tracking-wide)",
          textTransform: "uppercase",
          color: "var(--text-faint)",
        }}
      >
        <span role="columnheader">Gerät</span>
        {!narrow && <span role="columnheader">Typ</span>}
        {!narrow && <span role="columnheader">Quelle</span>}
        {!narrow && <span role="columnheader">Kanäle</span>}
        {/* Wo im Kit das Drei-Punkte-Menü steht, stand hier die Betriebsart —
            eine Angabe, die in dieser Anwendung an jedem Gerät „Cloud" heißt und
            nur die Mobile-App etwas angeht. An ihrer Stelle steht jetzt die
            Frage, die man an einer Geräteliste wirklich hat.

            **Die Überschrift heißt „Zustand" und nicht mehr „Erreichbarkeit"**,
            seit die Spalte zwei Marken tragen kann: ob sich das Gerät meldet und
            ob es aktuell ist. Eine Überschrift, die nur die eine von beiden
            benennt, macht aus der anderen einen Fremdkörper in ihrer Spalte. */}
        <span role="columnheader">Zustand</span>
        {!narrow && (
          <span role="columnheader">
            {/* Die Spalte des Chevrons trägt keine Überschrift — sie zeigt keinen
                Wert an, sondern wiederholt nur, wohin die Zeile führt. */}
            <span style={SR_ONLY}>Öffnen</span>
          </span>
        )}
      </div>

      {devices.map((device, index) => {
        const reachability = reachabilityBadge(device);
        /* Nur, wenn das Gerät es selbst gemeldet hat — sonst `null` und keine
           Marke. Bis hierher stand die Angabe ausschließlich in der
           Firmware-Kachel der Detailseite, also erst hinter zwei Klicks je
           Gerät; wer wissen wollte, ob irgendwo eines ansteht, öffnete jedes
           einzeln. */
        const update = updateBadge(device);
        const flooding = floodingBadge(device);

        return (
          <div
            key={device.id}
            role="row"
            style={{
              display: "grid",
              gridTemplateColumns: columns,
              gap: "var(--sp-3)",
              alignItems: "center",
              padding: "13px var(--sp-5)",
              borderBottom: index < devices.length - 1 ? "1px solid var(--border-subtle)" : "none",
              cursor: "pointer",
            }}
            /* Nur Mausbequemlichkeit — der bedienbare Teil ist der Link im Namen.
               Deshalb ohne `role="button"` und ohne Tabulatorstopp: Ein zweites
               Bedienelement über demselben Ziel ließe die Tastatur jede Zeile
               zweimal durchlaufen. */
            onClick={() => navigate(deviceRoute(siteId, device.id))}
            onMouseEnter={(event) => {
              event.currentTarget.style.background = "var(--surface-inset)";
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.background = "transparent";
            }}
          >
            <div
              role="cell"
              style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  borderRadius: "var(--radius-sm)",
                  background: "var(--surface-inset)",
                  color: "var(--text-muted)",
                }}
              >
                <icons.IconDevice size={17} />
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                  <a
                    href={routePath(deviceRoute(siteId, device.id))}
                    /* Der gewöhnliche Klick bleibt in der Anwendung; Strg-, Cmd-
                       und Mittelklick gehen an den Browser weiter und öffnen einen
                       neuen Tab. Ohne diese Prüfung wäre der Link nur Zierde. */
                    onClick={(event) => {
                      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
                        return;
                      }
                      event.preventDefault();
                      navigate(deviceRoute(siteId, device.id));
                    }}
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "var(--text-strong)",
                      textDecoration: "none",
                      ...CLIP,
                    }}
                  >
                    {device.name}
                  </a>
                  {/* Kein Punkt mehr neben dem Namen (UI-Durchsicht U8): Er wiederholte
                      die Marke am Zeilenende, und zwei Zustandsanzeigen in einer Zeile
                      lassen nach einem Unterschied suchen, den es nicht gibt. */}
                </span>
                <span
                  style={{
                    display: "block",
                    fontFamily: "var(--font-mono)",
                    fontSize: 11.5,
                    color: "var(--text-muted)",
                    ...CLIP,
                  }}
                >
                  {device.serialNumber}
                </span>
                {/* Schmal rücken die beiden Spalten daneben hierher — als
                    **eine** Zeile und nicht als zwei, damit die Zeilenhöhe der
                    Liste gleich bleibt. */}
                {narrow && (
                  <span
                    style={{ display: "block", fontSize: 12, color: "var(--text-muted)", ...CLIP }}
                  >
                    {productName(device)} · {sourceLabel(device, sources)}
                  </span>
                )}
              </span>
            </div>

            {/* Was das Gerät selbst gemeldet hat, sonst der Katalog — dieselbe
                Bezeichnung wie im Kopf seiner Detailseite (HM-041). */}
            {!narrow && (
              <span role="cell" title={productLine(device)} style={{ fontSize: 13, color: "var(--text-muted)", ...CLIP }}>
                {productName(device)}
              </span>
            )}

            {!narrow && (
              <span role="cell" style={{ fontSize: 13, color: "var(--text-muted)", ...CLIP }}>
                {sourceLabel(device, sources)}
              </span>
            )}

            {!narrow && (
              <span role="cell" style={{ fontSize: 13, color: "var(--text-muted)" }}>
                {/* Nur die Namen. Der Zusatz „(ohne Zähler)" ist mit HM-045
                  weggefallen: Ein Zähler hängt nicht am Kanal, sondern über eine
                  Bindung am Zählwerk — und ein Gerät ohne Kanäle stand hier
                  dauerhaft auf „keine", obwohl es sehr wohl etwas misst. Was ein
                  Gerät anbietet, steht auf seiner Detailseite. */}
                {device.channels.length === 0
                  ? "keine"
                  : device.channels.map((channel) => channel.name).join(" · ")}
              </span>
            )}

            <span role="cell" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {/* Als Text und nicht nur als farbiger Punkt — den liest eine
                  Sprachausgabe nicht, und Rot-Grün auch nicht jeder. */}
              <Badge tone={reachability.tone} title={reachability.title}>
                {reachability.label}
              </Badge>
              {/* **Hinter der Erreichbarkeit und nicht davor.** Ob sich ein Gerät
                  überhaupt meldet, geht dem voraus, ob es die neueste Firmware
                  hat — und an einem Gerät, das nicht erreichbar ist, führt der
                  Weg zum Update ohnehin über die erste Marke. */}
              {update !== null && (
                <Badge tone={update.tone} title={update.title}>
                  {update.label}
                </Badge>
              )}
              {flooding !== null && (
                <Badge tone={flooding.tone} title={flooding.title}>
                  {flooding.label}
                </Badge>
              )}
            </span>

            {/**
             * Wo im Kit die drei Punkte stehen, steht hier ein **Chevron**. Das
             * Menü dahinter gäbe es nicht — umbenennen und freigeben liegen auf
             * der Detailseite, und ein Menü mit einem Eintrag „Details" ist ein
             * Umweg zu dem, was der Klick auf die Zeile schon tut. Der Chevron
             * sagt stattdessen, was die Zeile ist: ein Weg nach unten.
             */}
            {!narrow && (
              <span
                role="cell"
                aria-hidden="true"
                style={{
                  display: "inline-flex",
                  justifyContent: "center",
                  color: "var(--text-faint)",
                }}
              >
                <icons.IconChevronRight size={16} />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}


/**
 * Woher die Werte kommen — einmal formuliert, weil es die Angabe breit in einer
 * eigenen Spalte und schmal unter dem Namen gibt.
 *
 * „Ohne" ist ein echter Zustand und kein fehlender Wert: Ein Gerät im
 * Betriebsmodus `local_only` meldet sich nirgends.
 */
function sourceLabel(device: Device, sources: ReadonlyMap<string, string>): string {
  if (device.integrationInstanceId === null) return "ohne";
  return sources.get(device.integrationInstanceId) ?? "unbekannt";
}

/** Die Adresse eines Geräts — einmal gebildet, für `href` und für `navigate`. */
function deviceRoute(siteId: string, deviceId: string): Route {
  return { kind: "device", siteId, deviceId, tab: "overview" };
}

/**
 * Ein Gerät hinzufügen — über den **Kopplungscode**, den es anzeigt (HM-035).
 *
 * Zwei Schritte, wie der Ablauf sie vorsieht: erst nachsehen, was hinter dem Code
 * steckt, dann bestätigen. Das Nachsehen ist kein Beiwerk — es ist die einzige
 * Gelegenheit, an der jemand merkt, dass er gerade das Gerät des Nachbarn seiner
 * Anlage zuordnet. Deshalb stehen Typ und Seriennummer groß da, bevor der Knopf
 * „Diesem Gerät zustimmen" erscheint.
 *
 * **Der alte Weg über Seriennummer und Beanspruchungscode ist hier nicht mehr
 * zu sehen** — der Endpunkt `POST /devices/claim` bleibt, die Maske dafür ist
 * weg. Er war für ein Gerät gedacht, das keinen Code anzeigen kann, weil es
 * nicht ins Netz kommt; ein solches Gerät erreicht aber auch diese Seite nicht,
 * denn es hat sich nie gemeldet. Wer ihn wieder braucht — etwa für ein
 * Fremdgerät über eine andere Integration —, baut ihn als abgesetzten zweiten
 * Block darunter, nicht als zweites Feld in dasselbe Formular: Zwei Wege
 * nebeneinander, von denen einer fast nie gemeint ist, lassen den Nutzer raten.
 */
function DeviceClaimForm({
  siteId,
  initialCode = null,
  onDone,
}: {
  siteId: string | null;
  initialCode?: string | null;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState(initialCode ?? "");
  const [error, setError] = useState<string | null>(null);

  /**
   * **Darf an diese Anlage ein Gateway?** — gefragt erst, wenn der Code zu einem
   * gehört (HM-109, Regel 10).
   *
   * Die Auskunft stand bis zum Umbau als eigener Abschnitt in der
   * Geräteverwaltung, auch an den allermeisten Anlagen, die nie ein Gateway
   * haben werden. Hier steht sie an der einzigen Stelle, an der die Frage
   * wirklich entsteht: Jemand hat einen Gateway-Code vor sich und will wissen,
   * ob er ihn hier bestätigen kann.
   *
   * **Sie erspart den Fehlschlag, sie ersetzt ihn nicht.** `POST …/approve`
   * prüft dieselbe Regel noch einmal, gegen die Datenbank und gegen die Rolle
   * dieses Nutzers.
   */
  const [found, setFound] = useState<ClaimRequestSummary | null>(null);

  /**
   * **An welche Anlage das Gerät kommt, wählt man hier** (Befund A2-2). Der
   * Kopplungslink des Geräts trägt keine Anlage, und der Rahmen öffnete ihn
   * unter der ersten — wer zwei Häuser führt, koppelte den Lesekopf aus dem
   * zweiten still an das erste. Vorgewählt ist die Anlage, unter der das
   * Formular steht; die Wahl erscheint erst ab der zweiten aktiven Anlage, denn
   * eine Auswahl mit einem Eintrag ist keine Frage.
   */
  const sites = useSites();
  const activeSites = (sites.data?.items ?? []).filter((site) => site.status === "active");
  const [chosenSite, setChosenSite] = useState<string | null>(null);
  const targetSite = chosenSite ?? siteId;

  const gateway = useSiteGateway(
    found !== null && isGatewayDevice(found.deviceType) ? targetSite : null,
  );
  const gatewayBlocked = gateway.data?.canLink === false;

  const lookUp = useMutation({
    mutationFn: async () => {
      const { data, error: failed } = await api.GET("/devices/claim-requests/{code}", {
        params: { path: { code: code.trim().toUpperCase() } },
      });
      if (failed || !data) {
        throw new Error(
          apiErrorMessage(failed) ??
            "Zu diesem Code gibt es keine Kopplung. Prüfe die Anzeige am Gerät.",
        );
      }
      return data;
    },
    onSuccess: (data) => {
      setFound(data);
      setError(null);
    },
    onError: (cause: Error) => {
      setFound(null);
      setError(cause.message);
    },
  });

  const approve = useMutation({
    mutationFn: async () => {
      if (targetSite === null) throw new Error("Lege zuerst eine Anlage an.");
      const { data, error: failed } = await api.POST("/devices/claim-requests/{code}/approve", {
        params: { path: { code: found!.code } },
        body: { siteId: targetSite, operatingMode: "cloud" },
      });
      if (failed || !data) {
        throw new Error(apiErrorMessage(failed) ?? "Das Gerät konnte nicht zugeordnet werden.");
      }
      return data;
    },
    onSuccess: async () => {
      setCode("");
      setFound(null);
      setError(null);
      // Beide Listen: Das neue Gerät hängt an der Cloud-Anbindung, und deren
      // Gerätezähler steht auf der Karte darüber.
      await queryClient.invalidateQueries({ queryKey: ["devices"] });
      await queryClient.invalidateQueries({ queryKey: ["integrations"] });
      onDone();
      // An eine andere Anlage gekoppelt: dorthin, wo das Gerät jetzt steht.
      if (targetSite !== null && targetSite !== siteId) {
        navigate({ kind: "site", siteId: targetSite, area: "devices" });
      }
    },
    onError: (cause: Error) => setError(cause.message),
  });

  /**
   * **Ablehnen ist eine echte Antwort** (Befund A3-1). Bis dahin gab es sie nur
   * über die API: Wer ein fremdes Gerät vor sich sah, konnte nur nicht
   * zustimmen, und der Code wartete seine 15 Minuten ab. Abgelehnt erfährt das
   * Gerät es beim nächsten Nachfragen und hört auf zu warten.
   */
  const deny = useMutation({
    mutationFn: async () => {
      const { data, error: failed } = await api.POST("/devices/claim-requests/{code}/deny", {
        params: { path: { code: found!.code } },
      });
      if (failed || !data) {
        throw new Error(apiErrorMessage(failed) ?? "Die Kopplung konnte nicht abgelehnt werden.");
      }
      return data;
    },
    onSuccess: (data) => {
      setFound(data);
      setError(null);
    },
    onError: (cause: Error) => setError(cause.message),
  });

  /**
   * Kam der Code aus dem Link, wird sofort gesucht — der Nutzer hat die Frage
   * „welches Gerät ist das?" mit dem Klick auf den Link schon gestellt.
   *
   * `lookedUp` verhindert, dass ein Fehlschlag in einer Schleife endet: `mutate`
   * ändert den Zustand, das Neuzeichnen liefe sonst erneut hier durch.
   */
  const [lookedUp, setLookedUp] = useState(false);
  useEffect(() => {
    if (initialCode === null || lookedUp) return;
    setLookedUp(true);
    lookUp.mutate();
  }, [initialCode, lookedUp, lookUp]);

  const expired = found !== null && found.status !== "pending";

  return (
    <Card
      title="Gerät hinzufügen"
      subtitle="Das Gerät zeigt nach dem Einschalten der Cloud-Anbindung einen sechsstelligen Code."
      padding="md"
    >
      <form
        style={{ display: "flex", alignItems: "flex-end", gap: "var(--sp-3)", flexWrap: "wrap" }}
        onSubmit={(event) => {
          event.preventDefault();
          lookUp.mutate();
        }}
      >
        <Input
          label="Kopplungscode"
          value={code}
          placeholder="z. B. K7M2QF"
          required
          // Der Code enthält keine verwechselbaren Zeichen (kein I, O, 0, 1) und
          // steht immer in Großbuchstaben — der Server nimmt beides an, aber wer
          // tippt, soll sofort sehen, dass er dasselbe schreibt wie das Display.
          style={{ flex: "1 1 200px", fontFamily: "var(--font-mono)", letterSpacing: "0.08em" }}
          onChange={(event) => setCode(event.target.value.toUpperCase())}
        />
        <Button type="submit" variant="secondary" disabled={lookUp.isPending || code.length === 0}>
          {lookUp.isPending ? "Sucht …" : "Gerät suchen"}
        </Button>
      </form>

      {found !== null && (
        <div
          style={{
            marginTop: "var(--sp-4)",
            padding: "var(--sp-4)",
            borderRadius: "var(--radius-md)",
            background: "var(--surface-inset)",
            display: "flex",
            alignItems: "center",
            gap: "var(--sp-4)",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: "var(--radius-md)",
              background: "var(--surface-base)",
              color: "var(--text-muted)",
            }}
          >
            <icons.IconDevice size={20} />
          </span>
          <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--text-strong)" }}>
              {deviceTypeLabel(found.deviceType)}
            </div>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 12.5,
                color: "var(--text-muted)",
              }}
            >
              {found.serialNumber}
              {found.firmwareVersion !== null && ` · Firmware ${found.firmwareVersion}`}
            </div>
          </div>
          {expired ? (
            <Badge tone="neutral">{CLAIM_STATUS_LABELS[found.status] ?? found.status}</Badge>
          ) : (
            <span style={{ display: "inline-flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
              <Button
                variant="ghost"
                disabled={deny.isPending || approve.isPending}
                onClick={() => deny.mutate()}
              >
                {deny.isPending ? "Wird abgelehnt …" : "Ablehnen"}
              </Button>
              <Button
                disabled={approve.isPending || deny.isPending || targetSite === null || gatewayBlocked}
                onClick={() => approve.mutate()}
              >
                {approve.isPending ? "Wird zugeordnet …" : "Diesem Gerät zustimmen"}
              </Button>
            </span>
          )}
        </div>
      )}

      {found !== null && !expired && activeSites.length > 1 && (
        <Select
          label="An diese Anlage"
          value={targetSite ?? ""}
          onChange={(event) => setChosenSite(event.target.value)}
          options={activeSites.map((site) => ({ value: site.id, label: site.name }))}
          style={{ marginTop: "var(--sp-3)", maxWidth: 360 }}
        />
      )}

      {found?.status === "denied" && (
        <p style={{ margin: "var(--sp-3) 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>
          Abgelehnt — das Gerät erfährt es beim nächsten Nachfragen und bekommt keine Zugangsdaten.
          War es doch das richtige, schalte die Cloud-Anbindung am Gerät noch einmal ein.
        </p>
      )}

      {found?.status === "expired" && (
        <p style={{ margin: "var(--sp-3) 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>
          Ein Kopplungscode gilt 15 Minuten. Schalte die Cloud-Anbindung am Gerät noch einmal
          ein — es zeigt dann einen neuen.
        </p>
      )}

      {/**
       * **Gesperrt und begründet, statt in die Ablehnung laufen zu lassen**
       * (HM-109). Der Knopf bleibt sichtbar — er ist der einzige Weg, den diese
       * Karte anbietet, und der Zustand ist behebbar: Ein gekauftes Gerät
       * koppeln oder das bestehende Gateway freigeben. Ausgeblendet würde
       * jemand den Weg suchen, den es gerade nicht gibt.
       */}
      {!expired && gatewayBlocked && (
        <div style={{ marginTop: "var(--sp-3)" }}>
          <Alert>{gatewayRefusalLabel(gateway.data?.refusal ?? null)}</Alert>
        </div>
      )}

      {siteId === null && (
        <p style={{ margin: "var(--sp-3) 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>
          Ein Gerät gehört immer zu einer Anlage. Lege zuerst eine an.
        </p>
      )}

      {error !== null && (
        <div style={{ marginTop: "var(--sp-3)" }}>
          <Alert>{error}</Alert>
        </div>
      )}
    </Card>
  );
}

/** Warum ein Code nicht mehr gilt — in Worten, nicht als Rohwert. */
const CLAIM_STATUS_LABELS: Record<string, string> = {
  approved: "bereits bestätigt",
  consumed: "bereits verbunden",
  denied: "abgelehnt",
  expired: "abgelaufen",
};

/** Einzeilig abschneiden statt umbrechen — sonst springt die Zeilenhöhe der Tabelle. */
const CLIP = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

/**
 * Nur für die Sprachausgabe.
 *
 * `display: none` täte es **nicht** — was so ausgeblendet ist, liest auch eine
 * Sprachausgabe nicht. Eine Spaltenüberschrift ohne Text wäre in einer Tabelle
 * aber genau die Lücke, an der jemand hängen bleibt.
 */
const SR_ONLY = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap",
} as const;
